<template>
  <base-modal hide-footer :title="$t('street_lights.edit')">
    <div class="col-sm-12">
      <span class="hint-text">{{ $t('street_lights.bulk_edit_hint') }}</span>
      <div v-if="light"
        class="col-sm-12 editor-form"
      >
        <h4 class="form-header">
          {{ $t('street_lights.basic_info') }}
        </h4>
        <div class="row">
          <div class="col-sm-4">
            <span class="span-title">{{ $t('street_lights.status') }}</span>
              <select
                  id="lightStatus"
                  v-model="light.status"
                  class="form-select form-select-sm"
              >
                  <option :value="null">
                      {{ $t('street_lights.select_status') }}
                  </option>
                  <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                      {{ option.text }}
                  </option>
              </select>
          </div>
          <div class="col-sm-4">
            <span class="span-title">{{ $t('street_lights.lighting_class') }}</span>
              <select
                  id="lightClass"
                  v-model="light.lighting_class"
                  class="form-select form-select-sm"
              >
                  <option v-for="option in lightingClassOptions" :key="option.value" :value="option.value">
                      {{ option.text }}
                  </option>
              </select>
          </div>
          <div class="col-sm-4">
            <span class="span-title">{{ $t('street_lights.construction_year') }}</span>
              <input
                  id="lightYear"
                  v-model="light.construction_year"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
              />
          </div>
        </div>
        <hr>
        <h4 class="form-header mt-0 pt-0">
          {{ $t('power_stations.contract') }}
        </h4>
        <span class="tip-light">{{ $t('power_stations.contract_tip') }}</span>
        <open-contract-filter
            :from="fromDate"
            :to="toDate"
            :contract="preselectedContract"
            :show-company="false"
            :show-title="false"
            style="margin-left: -2px; margin-right: -3px;"
            @loading="loading=true"
            @loading-completed="loading=false"
            @contract-selected="setContract"
        />
        <hr>
        <div class="row">
            <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                <div class="flex-grow-1">
                    <h4 class="form-header pointer hover-header" @click.stop="lightInfoVisible = !lightInfoVisible">{{ $t('street_lights.light_info') }}</h4>
                </div>
                <div class="ms-2 px-2" @click.stop="lightInfoVisible = !lightInfoVisible">
                    <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: lightInfoVisible }"/>
                </div>
            </div>
            <div class="routa-collapse" :class="{ collapsed: lightInfoVisible }">
                <div class="row">
                    <div class="col-sm-6">
                        <span class="span-title">{{ $t('street_lights.light_type') }}</span>
                        <select
                            id="lightType"
                            v-model="light.light_type"
                            class="form-select form-select-sm"
                        >
                            <option v-for="option in lightTypeOptions" :key="option.value" :value="option.value">
                                {{ option.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-6">
                        <span class="span-title">{{ $t('street_lights.power_rating') }}</span>
                        <input
                            id="powerRating"
                            v-model="light.power_rating"
                            type="number"
                            class="form-control form-control-sm"
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-6">
                        <span class="span-title">{{ $t('street_lights.light_manufacturer') }}</span>
                        <input
                            id="lightManufacturer"
                            v-model="light.light_manufacturer"
                            type="text"
                            class="form-control form-control-sm"
                        />
                    </div>
                    <div class="col-sm-6">
                        <span class="span-title">{{ $t('street_lights.model') }}</span>
                        <input
                            id="lightModel"
                            v-model="light.model"
                            type="text"
                            class="form-control form-control-sm"
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.installation') }}</span>
                        <select
                            id="lightInstallation"
                            v-model="light.installation"
                            class="form-select form-select-sm"
                        >
                            <option v-for="option in installOptions" :key="option.value" :value="option.value">
                                {{ option.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.optics') }}</span>
                        <input
                            id="optics"
                            v-model="light.optics"
                            type="text"
                            class="form-control form-control-sm"
                        />
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.dimming_profile') }}</span>
                        <input
                            id="dimmingProfile"
                            v-model="light.dimming_profile"
                            type="checkbox"
                            class="form-check-input form-check-lg"
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.bulb_type') }}</span>
                        <select
                            id="lightLampType"
                            v-model="light.bulb_type"
                            class="form-select form-select-sm"
                        >
                            <option v-for="option in bulbTypeOptions" :key="option.value" :value="option.value">
                                {{ option.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.bulb_count') }}</span>
                        <input
                            id="bulbCount"
                            v-model="light.bulb_count"
                            type="text"
                            class="form-control form-control-sm"
                        />
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.bulb_manufacturer') }}</span>
                        <input
                            id="bulbManufacturer"
                            v-model="light.bulb_manufacturer"
                            type="text"
                            class="form-control form-control-sm"
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.guarantee_basis') }}</span>
                        <select
                            id="guaranteeBasis"
                            v-model="light.guarantee_basis"
                            class="form-select form-select-sm"
                        >
                            <option v-for="option in guaranteeOptions" :key="option.value" :value="option.value">
                                {{ option.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.guarantee_expiration') }}</span>
                        <input
                            id="guaranteeExp"
                            v-model="light.guarantee_expiration"
                            type="date"
                            class="form-control form-control-sm"
                        />
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('street_lights.guarantor') }}</span>
                        <input
                            id="guarantor"
                            v-model="light.guarantor"
                            type="text"
                            class="form-control form-control-sm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <hr>
        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
              <div class="flex-grow-1">
                  <h4 class="form-header pointer hover-header" @click.stop="poleVisible = !poleVisible">{{ $t('street_lights.pole_info') }}</h4>
              </div>
              <div class="ms-2 px-2" @click.stop="poleVisible = !poleVisible">
                  <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: poleVisible }"/>
              </div>
          </div>
          <div class="routa-collapse" :class="{ collapsed: poleVisible }">
              <div class="row">
                  <div class="col-sm-4">
                      <span class="span-title">{{ $t('street_lights.pole_type') }}</span>
                      <select
                          id="poleType"
                          v-model="light.pole_type"
                          class="form-select form-select-sm"
                      >
                          <option v-for="option in poleTypeOptions" :key="option.value" :value="option.value">
                              {{ option.text }}
                          </option>
                      </select>
                  </div>
                  <div class="col-sm-4">
                      <span class="span-title">{{ $t('street_lights.pole_manufacturer') }}</span>
                      <input
                          id="poleManufacturer"
                          v-model="light.pole_manufacturer"
                          type="text"
                          class="form-control form-control-sm"
                      />
                  </div>
                  <div class="col-sm-4">
                      <span class="span-title">{{ $t('street_lights.installation_height') }}</span>
                      <input
                          id="installHght"
                          v-model="light.installation_height"
                          step="any"
                          type="number"
                          min="0"
                          class="form-control form-control-sm"
                      />
                  </div>
              </div>
              <div class="row">
                  <div class="col-sm-6">
                      <span class="span-title">{{ $t('street_lights.stand') }}</span>
                      <input
                          id="stand"
                          v-model="light.stand"
                          type="text"
                          class="form-control form-control-sm"
                      />
                  </div>
                  <div class="col-sm-6">
                      <span class="span-title">{{ $t('street_lights.supporting_structure') }}</span>
                      <select
                          id="supportStruct"
                          v-model="light.supporting_structure"
                          class="form-select form-select-sm"
                      >
                          <option v-for="option in poleSupportOptions" :key="option.value" :value="option.value">
                              {{ option.text }}
                          </option>
                      </select>
                  </div>
              </div>
              <div class="row">
                  <div class="col-sm-6">
                      <span class="span-title">{{ $t('street_lights.arm_type') }}</span>
                      <select
                          id="armType"
                          v-model="light.arm_type"
                          class="form-select form-select-sm"
                      >
                          <option v-for="option in armTypeOptions" :key="option.value" :value="option.value">
                              {{ option.text }}
                          </option>
                      </select>
                  </div>
                  <div class="col-sm-6">
                      <span class="span-title">{{ $t('street_lights.arm_length') }}</span>
                      <input
                          id="armLength"
                          v-model="light.arm_length"
                          step="any"
                          type="number"
                          min="0"
                          class="form-control form-control-sm"
                      />
                  </div>
              </div>
              <div class="row">
                  <div class="col-sm-6">
                      <span class="span-title">{{ $t('street_lights.cabling') }}</span>
                      <select
                          id="lightCabling"
                          v-model="light.cabling"
                          class="form-select form-select-sm"
                      >
                          <option v-for="option in cablingOptions" :key="option.value" :value="option.value">
                              {{ option.text }}
                          </option>
                      </select>
                  </div>
                  <div class="col-sm-6">
                      <span class="span-title">{{ $t('street_lights.earthing') }}</span>
                      <select
                          id="lightEarthing"
                          v-model="light.earthing"
                          class="form-select form-select-sm"
                      >
                          <option v-for="option in earthingOptions" :key="option.value" :value="option.value">
                              {{ option.text }}
                          </option>
                      </select>
                  </div>
              </div>
      </div>
          <hr/>
        <div class="col-sm-12 text-end">
          <button
            class="btn btn-secondary form-button"
            @click.stop="$emit('close')"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            class="btn btn-success form-button"
            :disabled="loading||light===null"
            @click.stop="updateLights"
          >
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
      <div
        v-if="loading"
        id="loader"
        class="spinner"
      />
    </div>
  </base-modal>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import BaseModal from '../BaseModal'
import OpenContractFilter from "@/components/contract/OpenContractFilter";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {streetLightHelper} from "@/components/mixins/StreetLightMixin";

export default {
    name: 'StreetLightBulkEditor',
    components: {OpenContractFilter, BaseModal},
    mixins: [restApi, timeUtils, streetLightHelper],
    props: {
        selectedContract: {
            type: Object,
            default: null
        },
        coordinate: {
            type: Object,
            default: null
        },
        streetLights: {
            type: Array,
            default: null
        }
    },
    data: function () {
        return {
            loading: false,
            alertDismissSecs: 5,
            alertDismissCountDown: 0,
            alertMessage: '',
            light: null,  // A single street light object representing the values to be set for given ids
            contract: null,
            fullScreenPhotoData: null,
            photosVisible: true,
            lightInfoVisible: false,
            poleVisible: false,
            maintenanceVisible: false,
            photos: new Array(5),
            selectedStation: null,
            stations: [],
            lightsDetails: [],
            variant: 'danger',
            typeOptions: [
                {text: 'Type 1', value: 1},
                {text: 'Type 2', value: 2},
                {text: 'Type 4', value: 3}
            ]
        }
    },
    computed: {
        fromDate() {
            if (this.light && this.light.created_at) {
                return this.light.created_at
            }
            let d = this.getStartOfTheDay(new Date())
            return d.toISOString()
        },
        toDate() {
            let d = new Date()
            return d.toISOString()
        },
        preselectedContract() {
            return this.light && this.light.contract ? this.light.contract : null
        }
    },
    emits: ['close', 'closeAndUpdate'],
    mounted() {
        this.fetchDetails()
        this.fetchStations()
    },
    methods: {
        initValues() {
            this.light = {
                construction_year: this.getCommonValue('construction_year'),
                lighting_class: this.getCommonValue('lighting_class'),
                light_manufacturer: this.getCommonValue('light_manufacturer'),
                light_type: this.getCommonValue('light_type'),
                arm_length: this.getCommonValue('arm_length'),
                arm_type: this.getCommonValue('arm_type'),
                bulb_count: this.getCommonValue('bulb_count'),
                bulb_manufacturer: this.getCommonValue('bulb_manufacturer'),
                bulb_type: this.getCommonValue('bulb_type'),
                guarantee_basis: this.getCommonValue('guarantee_basis'),
                guarantee_expiration: this.timestampToDateInput(this.getCommonValue('guarantee_expiration')),
                guarantor: this.getCommonValue('guarantor'),
                installation_height: this.getCommonValue('installation_height'),
                pole_type: this.getCommonValue('pole_type'),
                supporting_structure: this.getCommonValue('supporting_structure'),
                stand: this.getCommonValue('stand'),
//                power_station: this.getCommonValue('power_station'),
                status: this.getCommonValue('status'),
                contract: this.getCommonValue('contract'),
                earthing: this.getCommonValue('earthing'),
                cabling: this.getCommonValue('cabling'),
                optics: this.getCommonValue('optics'),
                installation: this.getCommonValue('installation'),
                power_rating: this.getCommonValue('power_rating'),
                model: this.getCommonValue('model'),
                dimming_profile: this.getCommonValue('dimming_profile'),
                pole_manufacturer: this.getCommonValue('pole_manufacturer')
            }
        },
        getCommonValue(propName) {
            let value = null
            for (let i = 0; i < this.lightsDetails.length; i++ ) {
                let item = this.lightsDetails[i]
                if (!item[propName]) {
                    return null
                }
                if (value && typeof value !== typeof item[propName]) {
                    // Type of value is different than the item's property
                    return null
                }
                if (typeof item[propName] === 'object') {
                    let propertyValue = item[propName]['id']
                    if (value && propertyValue !== value.id) {
                        // Items have different values
                        return null
                    }
                } else {
                    let propertyValue = item[propName]
                    if (!propertyValue || (value && propertyValue !== value)) {
                        // Items have different values
                        return null
                    }
                }
                value = item[propName]
            }
            return value
        },
        fetchDetails() {
            this.loading = true
            this.stations = []
            this.restFetchParams(this.streetLightsDetailsUrl, {lights: this.getLightIds()}, this.handleDetails, () => {
                this.loading = false
            })
        },
        handleDetails(response) {
              if (response) {
                  this.lightsDetails = response.data
                  this.initValues()
              }
              this.loading = false
        },
        fetchStations() {
            this.loading = true
            this.stations = []
            this.restFetch(this.powerStationUrl, this.handleSearch, () => {
                this.loading = false
            })
        },
        handleSearch: function (response) {
            if (response && response.data) {
                response.data.forEach(station => {
                    this.stations.push({value: station.id, text: station.id + ' ' + station.street})
                })
            }
            this.loading = false
        },
        showAlert: function (message, variant) {
            this.alertDismissCountDown = this.alertDismissSecs
            this.alertMessage = message
            if (variant) {
                this.variant = variant
            } else {
                this.variant = 'danger'
            }
        },
        alertCountDownChanged(dismissCountDown) {
            this.alertDismissCountDown = dismissCountDown
        },
        updateLights: function () {
            this.loading = true
            if (this.contract) {
                this.light.contract = this.contract
            }
            let params = {
                data: this.light,
                lights : this.getLightIds()
            }
            this.restUpdate(this.streetLightBulkUrl, params, this.success, this.fail)
        },
        getLightIds() {
            return this.streetLights.map((item) => item.id)
        },
        success: function () {
            this.loading = false
            this.$emit('closeAndUpdate')
        },
        fail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_add_failed'))
        },
        setContract: function (contract) {
            this.contract = contract
        }
    }
}
</script>
<style>
@import "../../styles/modal.css";
</style>
